import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';

import Icon from '../../components/AppIcon';
import MetricCard from '../member-dashboard/components/MetricCard';
import ReferralCodeCard from './components/ReferralCodeCard';
import ReferralTrackingList from './components/ReferralTrackingList';
import RewardsList from './components/RewardsList';
import ReferralLeaderboard from './components/ReferralLeaderboard';
import ShareModal from './components/ShareModal';

const MemberReferralSystem = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isAuthenticated, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [showShareModal, setShowShareModal] = useState(false);
  
  // Data states
  const [referralCode, setReferralCode] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [stats, setStats] = useState({
    totalReferrals: 0,
    successfulReferrals: 0,
    pendingReferrals: 0,
    totalRewardsEarned: 0,
    availableRewards: 0
  });

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (user?.id && currentTenant?.id) {
      loadReferralData();
      setupRealtimeSubscriptions();
    }
  }, [user, currentTenant]);

  const loadReferralData = async () => {
    setLoading(true);
    setError('');

    try {
      // Load or create referral code
      let { data: codeData, error: codeError } = await supabase
        ?.from('referral_codes')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.eq('tenant_id', currentTenant?.id)
        ?.eq('is_active', true)
        ?.single();

      if (codeError && codeError?.code !== 'PGRST116') {
        throw codeError;
      }

      // Create referral code if doesn't exist
      if (!codeData) {
        const { data: newCode, error: createError } = await supabase
          ?.rpc('generate_referral_code', {
            p_user_id: user?.id,
            p_tenant_id: currentTenant?.id
          });

        if (createError) throw createError;

        const { data: insertedCode, error: insertError } = await supabase
          ?.from('referral_codes')
          ?.insert({
            tenant_id: currentTenant?.id,
            user_id: user?.id,
            code: newCode,
            is_active: true
          })
          ?.select()
          ?.single();

        if (insertError) throw insertError;
        codeData = insertedCode;
      }

      setReferralCode(codeData);

      // Load referrals
      const { data: referralsData, error: referralsError } = await supabase
        ?.from('referrals')
        ?.select('*')
        ?.eq('referrer_id', user?.id)
        ?.eq('tenant_id', currentTenant?.id)
        ?.order('created_at', { ascending: false });

      if (referralsError) throw referralsError;
      setReferrals(referralsData || []);

      // Load rewards
      const { data: rewardsData, error: rewardsError } = await supabase
        ?.from('referral_rewards')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.eq('tenant_id', currentTenant?.id)
        ?.order('created_at', { ascending: false });

      if (rewardsError) throw rewardsError;
      setRewards(rewardsData || []);

      // Load leaderboard
      const { data: leaderboardData, error: leaderboardError } = await supabase
        ?.from('referral_leaderboard')
        ?.select('*, user_profiles(full_name, avatar_url)')
        ?.eq('tenant_id', currentTenant?.id)
        ?.gte('period_end', new Date()?.toISOString())
        ?.order('rank', { ascending: true })
        ?.limit(10);

      if (leaderboardError) throw leaderboardError;
      setLeaderboard(leaderboardData || []);

      // Calculate stats
      const totalReferrals = referralsData?.length || 0;
      const successfulReferrals = referralsData?.filter(r => r?.status === 'active_member')?.length || 0;
      const pendingReferrals = referralsData?.filter(r => r?.status === 'pending' || r?.status === 'signed_up')?.length || 0;
      const totalRewardsEarned = rewardsData?.reduce((sum, r) => sum + parseFloat(r?.reward_value || 0), 0) || 0;
      const availableRewards = rewardsData?.filter(r => r?.status === 'earned')?.reduce((sum, r) => sum + parseFloat(r?.reward_value || 0), 0) || 0;

      setStats({
        totalReferrals,
        successfulReferrals,
        pendingReferrals,
        totalRewardsEarned,
        availableRewards
      });

    } catch (err) {
      console.error('Error loading referral data:', err);
      setError(err?.message || 'Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscriptions = () => {
    const referralsChannel = supabase
      ?.channel('referrals-changes')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'referrals',
          filter: `referrer_id=eq.${user?.id}`
        },
        () => {
          loadReferralData();
        }
      )
      ?.subscribe();

    const rewardsChannel = supabase
      ?.channel('rewards-changes')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'referral_rewards',
          filter: `user_id=eq.${user?.id}`
        },
        () => {
          loadReferralData();
        }
      )
      ?.subscribe();

    return () => {
      referralsChannel?.unsubscribe();
      rewardsChannel?.unsubscribe();
    };
  };

  const handleCopyCode = () => {
    if (referralCode?.code) {
      navigator?.clipboard?.writeText(referralCode?.code);
      // Show success notification (could integrate with notification system)
    }
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading referral system...</p>
          </div>
        </div>
        <MobileBottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-6 md:py-8 pb-24 md:pb-8">
        {/* Page Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-primary/10">
              <Icon name="Users" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
                Member Referral System
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Invite friends and earn rewards
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* Stats Overview */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
          <MetricCard
            icon="Users"
            iconColor="var(--color-primary)"
            title="Total Referrals"
            value={stats?.totalReferrals?.toString()}
            unit="referrals"
            subtitle="All time referrals sent"
            trend="up"
            trendValue={`+${stats?.totalReferrals}`}
            onClick={() => {}}
          />
          <MetricCard
            icon="CheckCircle"
            iconColor="var(--color-success)"
            title="Successful"
            value={stats?.successfulReferrals?.toString()}
            unit="members"
            subtitle="Active member conversions"
            trend="up"
            trendValue={`${Math.round((stats?.successfulReferrals / Math.max(stats?.totalReferrals, 1)) * 100)}%`}
            onClick={() => {}}
          />
          <MetricCard
            icon="Clock"
            iconColor="var(--color-warning)"
            title="Pending"
            value={stats?.pendingReferrals?.toString()}
            unit="referrals"
            subtitle="Awaiting conversion"
            trend={undefined}
            trendValue={undefined}
            onClick={() => {}}
          />
          <MetricCard
            icon="Gift"
            iconColor="var(--color-accent)"
            title="Rewards Earned"
            value={`${stats?.totalRewardsEarned?.toFixed(2)}`}
            subtitle={`${stats?.availableRewards?.toFixed(2)} available`}
            trend="up"
            trendValue={`+${stats?.totalRewardsEarned?.toFixed(2)}`}
            unit={undefined}
            onClick={() => {}}
          />
        </div>

        {/* Referral Code Card */}
        <div className="mb-6 md:mb-8">
          <ReferralCodeCard
            code={referralCode?.code}
            usesCount={referralCode?.uses_count}
            onCopy={handleCopyCode}
            onShare={handleShare}
          />
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="flex gap-2 border-b border-border overflow-x-auto">
            {[
              { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
              { id: 'tracking', label: 'Tracking', icon: 'Activity' },
              { id: 'rewards', label: 'Rewards', icon: 'Gift' },
              { id: 'leaderboard', label: 'Leaderboard', icon: 'Trophy' }
            ]?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`
                  flex items-center gap-2 px-4 py-3 font-medium text-sm whitespace-nowrap
                  border-b-2 transition-colors
                  ${activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                  }
                `}
              >
                <Icon name={tab?.icon} size={16} />
                {tab?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h2 className="text-lg font-semibold text-foreground mb-4">Recent Referrals</h2>
                <ReferralTrackingList referrals={referrals?.slice(0, 5)} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground mb-4">Available Rewards</h2>
                <RewardsList rewards={rewards?.filter(r => r?.status === 'earned')?.slice(0, 5)} onReload={loadReferralData} />
              </div>
            </div>
          )}

          {activeTab === 'tracking' && (
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-4">All Referrals</h2>
              <ReferralTrackingList referrals={referrals} />
            </div>
          )}

          {activeTab === 'rewards' && (
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-4">All Rewards</h2>
              <RewardsList rewards={rewards} onReload={loadReferralData} />
            </div>
          )}

          {activeTab === 'leaderboard' && (
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-4">Top Referrers This Month</h2>
              <ReferralLeaderboard leaderboard={leaderboard} currentUserId={user?.id} />
            </div>
          )}
        </div>
      </main>

      <MobileBottomNav />

      {/* Share Modal */}
      {showShareModal && (
        <ShareModal
          code={referralCode?.code}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </div>
  );
};

export default MemberReferralSystem;